import React, { MutableRefObject, useCallback, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import { getId } from '../../../utils';
import { TypeaheadField, type TypeaheadDropdownApi } from '../select/typeahead/typeahead-field';
import { useAddressSuggestionService } from './hooks/use-address-suggestion-service';
import type { TextFieldProps } from '../text-field/text-field';
import type { AddressFieldEvent, FieldEventDataProps, IFieldEventFunction } from './types';
import type { AddressFieldOptionMeta } from './utils';
import type { DawaSuggestion } from './hooks/use-dawa-autocomplete';

type AddressOptionMeta = AddressFieldOptionMeta<DawaSuggestion>;
type RenderOptionPayload = Parameters<NonNullable<ComponentProps<typeof TypeaheadField>['renderOption']>>[0];

type AddressFieldProps = {
    isSubmitted?: boolean;
    fieldEvent: AddressFieldEvent;
    allowCustomAddress?: boolean;
    renderCustomOptionLabel?: (value: string) => React.ReactNode;
} & TextFieldProps;

const AddressField = React.forwardRef<HTMLInputElement, Omit<AddressFieldProps, 'maxLength'>>(
    (
        { id, label = 'Adresse', fieldEvent, errorText = 'er ugyldig', isSubmitted, isValid, onReset: _ignoredReset, allowCustomAddress, renderCustomOptionLabel, ...rest },
        forwardedRef
    ) => {
        const [generatedId] = useState(id ?? `addressfield-${getId()}`);
        const displayInputRef = useRef<HTMLInputElement | null>(null);

        const dropdownApiRef = useRef<TypeaheadDropdownApi | null>(null);

        const assignDisplayRef = useCallback(
            (node: HTMLInputElement | null) => {
                displayInputRef.current = node;
                if (!forwardedRef) return;
                if (typeof forwardedRef === 'function') {
                    forwardedRef(node);
                } else {
                    (forwardedRef as MutableRefObject<HTMLInputElement | null>).current = node;
                }
            },
            [forwardedRef]
        );

        const {
            options,
            matchOption,
            isLoading: isAddressLoading,
            status: serviceStatus,
            selectedOptionValue,
            validationStrategy,
            dropdownCloseStrategy,
            shouldAutoOpen,
            shouldOpenOnFocus,
            handleInputValueChange,
            handleHiddenInputChange,
            handleFocus,
            handleBlur,
            reset: resetService,
            interactionContext,
        } = useAddressSuggestionService({
            fieldEvent,
            dropdownApiRef,
            displayInputRef,
            allowCustomAddress,
            renderCustomOptionLabel,
        });

        const hasSelection = Boolean(selectedOptionValue);
        let computedIsValid = isValid;

        if (serviceStatus === 'valid') {
            computedIsValid = true;
        } else if (serviceStatus === 'custom') {
            computedIsValid = true;
        } else if (serviceStatus === 'invalid') {
            computedIsValid = false;
        } else if (isSubmitted && !hasSelection) {
            computedIsValid = false;
        }

        const handleReset = useCallback(() => {
            resetService();
            if (displayInputRef.current) {
                displayInputRef.current.dispatchEvent(new Event('reset', { bubbles: true }));
            }
        }, [resetService]);

        const renderAddressOption = useCallback(({ option }: RenderOptionPayload) => {
            const meta = option.meta as AddressOptionMeta | undefined;
            if (meta?.kind === 'custom') {
                return meta.displayLabel ?? option.label;
            }
            return null;
        }, []);

        return (
            <TypeaheadField
                id={generatedId}
                label={label}
                options={options}
                value={selectedOptionValue}
                matchOption={matchOption}
                onInputValueChange={interactionContext ? undefined : handleInputValueChange}
                onChange={handleHiddenInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                errorText={errorText}
                isValid={computedIsValid}
                isLoading={isAddressLoading}
                validationStrategy={validationStrategy}
                dropdownCloseStrategy={dropdownCloseStrategy}
                preserveSelectedOption={true}
                onReset={handleReset}
                wrapperClassName="autocomplete-container"
                inputRef={assignDisplayRef}
                openOnFocus={shouldOpenOnFocus}
                dropdownApiRef={dropdownApiRef}
                shouldAutoOpen={shouldAutoOpen}
                renderOption={allowCustomAddress ? renderAddressOption : undefined}
                interactionContext={interactionContext}
                {...rest}
            />
        );
    }
);

AddressField.displayName = 'AddressField';

export type { AddressFieldProps, IFieldEventFunction, FieldEventDataProps };
export { AddressField };
export default AddressField;
